import { For, JSX } from "solid-js";
import { useTelescopeContext } from "@hubble/telescope";
import { goBack } from "~/shared_states/modal";
import { hubbleAssuredBigger } from "~/assets/assets";

function BulletIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="18"
      viewBox="0 0 20 18"
      fill="none"
      class="shrink-0"
    >
      <circle cx="4" cy="9" r="3" class="fill-text-normal-secondary" />
    </svg>
  );
}

function KeyRefreshIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      class="shrink-0"
    >
      <path
        d="M14.1663 7.50016L16.6663 10.0002M16.6663 10.0002L14.1663 12.5002M16.6663 10.0002H9.99967M9.99967 16.6668C6.31778 16.6668 3.33301 13.6821 3.33301 10.0002C3.33301 6.31826 6.31778 3.3335 9.99967 3.3335"
        class="stroke-[var(--color-green-900)]"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function LightningIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      class="shrink-0"
    >
      <path
        d="M10.8337 1.6665L3.41699 11.6665H10.0003L9.16699 18.3332L16.5837 8.33317H10.0003L10.8337 1.6665Z"
        class="stroke-[var(--color-green-900)]"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function OurPromiseSection() {
  return (
    <div class="w-full rounded-2xl border border-stroke-1 bg-gradient-to-r from-[#30C0590D] to-[#EEFFF300]">
      <div class="flex w-full flex-col gap-[17px] overflow-clip p-5">
        <p class="text-title-5-semi-bold text-success-base">Our promise</p>

        <div class="h-px w-full bg-stroke-1" />

        <div class="flex flex-col gap-5">
          {/* Easy refunds section */}
          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-2">
              <KeyRefreshIcon />
              <p class="text-title-6-semi-bold text-success-base">
                Easy refunds
              </p>
            </div>
            <div class="flex items-start gap-2">
              <BulletIcon />
              <p class="flex-1 text-para-3-regular text-text-normal-secondary">
                If you want to return unused brand card (not used in a
                transaction and not added to wallet)
              </p>
            </div>
            <div class="flex items-start gap-2">
              <BulletIcon />
              <p class="flex-1 text-para-3-regular text-text-normal-secondary">
                If you face any technical issue while using the brand card
              </p>
            </div>
          </div>

          {/* Fast resolution section */}
          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-2">
              <LightningIcon />
              <p class="text-title-6-semi-bold text-success-base">
                Fast resolution
              </p>
            </div>
            <div class="flex items-start gap-2">
              <BulletIcon />
              <p class="flex-1 text-para-3-regular text-text-normal-secondary">
                Get your money back within 3 days of contacting us
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const howItWorksSteps = [
  <>
    Contact us via chat support or email us at{" "}
    <a
      href="mailto:support@hubble.com"
      class="underline decoration-solid underline-offset-2"
    >
      support@hubble.com
    </a>{" "}
    within 3 days of purchasing your brand card.
  </>,
  "Share your issue (error screenshot will be needed in case of technical issue).",
  "Provide details of brand, voucher amount and details (number & pin).",
  "Allow 3 days for us to check and process your refund.",
];

function SerialNumber(props: { number: number }) {
  return (
    <div class="flex size-5 shrink-0 items-center justify-center rounded-full bg-background-normal-tertiary">
      <span class="text-label-medium text-text-normal-secondary">
        {props.number}
      </span>
    </div>
  );
}

function HowItWorksSection() {
  return (
    <div class="w-full rounded-2xl border border-stroke-1 bg-background-normal-primary">
      <div class="flex w-full flex-col gap-4 overflow-clip p-5">
        <p class="text-title-5-semi-bold text-text-normal-primary">
          How it works
        </p>

        <div class="h-px w-full bg-stroke-1" />

        <div class="flex flex-col gap-4">
          <For each={howItWorksSteps}>
            {(step, index) => (
              <div class="flex w-full items-start gap-3">
                <SerialNumber number={index() + 1} />
                <p class="flex-1 whitespace-pre-wrap text-para-3-regular text-text-normal-primary">
                  {step}
                </p>
              </div>
            )}
          </For>
        </div>
      </div>
    </div>
  );
}

function AssuredIcon() {
  return (
    <img
      src={hubbleAssuredBigger}
      alt="Hubble Assured"
      class="size-[60px] object-contain"
    />
  );
}

export function AssuredDetailsModal(): JSX.Element {
  const { components } = useTelescopeContext();

  return (
    <div class="flex flex-col ">
      <components.ModalHeader
        size="large"
        title="Hubble assured"
        description="100% money back guarantee"
        icon={AssuredIcon}
        onClose={() => goBack()}
      />

      <div class="flex flex-col gap-4 px-5 pb-5">
        <OurPromiseSection />
        <HowItWorksSection />
      </div>
    </div>
  );
}
